if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();
      }

      hide() {
        super.hide();
      }

      show(opener) {
        super.show(opener);
        this.showActiveMedia();
        this.zoom();
      }

      showActiveMedia() {
        this.querySelectorAll(
          `[data-media-id]:not([data-media-id="${this.openedBy.getAttribute('data-media-id')}"])`
        ).forEach((element) => {
          element.classList.remove('active');
        });
        const activeMedia = this.querySelector(
          `[data-media-id="${this.openedBy.getAttribute('data-media-id')}"]`
        );
        const activeMediaTemplate = activeMedia.querySelector('template');
        const activeMediaContent = activeMediaTemplate ? activeMediaTemplate.content : null;
        activeMedia.classList.add('active');
        activeMedia.scrollIntoView();

        const container = this.querySelector('[role="document"]');
        container.scrollLeft = (activeMedia.width - container.clientWidth) / 2;

        if (
          activeMedia.nodeName == 'DEFERRED-MEDIA' &&
          activeMediaContent &&
          activeMediaContent.querySelector('.js-youtube')
        )
          activeMedia.loadContent();
      }

      zoom() {
        const container = this.querySelector('.product-media-modal__content');
        // Select ONLY the image with the 'active' class
        const image = container ? container.querySelector('img.active') : null;

        if (!container || !image) {
          return;
        }

        // --- Zoom State Variables ---
        let scale = 1;
        let offsetX = 0;
        let offsetY = 0;
        let lastScale = 1;
        let lastOffsetX = 0;
        let lastOffsetY = 0;

        // Gesture Variables
        let initialDistance = 0;
        // These are the VIEWPORT coordinates used for stable anchor point calculation.
        let initialMidpointX_Viewport = 0;
        let initialMidpointY_Viewport = 0;

        let startX = 0; // Used for single-finger pan start calculation
        let startY = 0;
        let lastTap = 0;
        let isPanning = false; // Flag to control drag state

        const MAX_SCALE = 3;
        const MIN_SCALE = 1;
        const DOUBLE_TAP_DELAY = 300;

        const applyTransform = () => {
          image.style.transform = `translate3d(${offsetX}px, ${offsetY}px, 0) scale(${scale})`;
        };

        const getDistance = (touches) => {
          const dx = touches[0].clientX - touches[1].clientX;
          const dy = touches[0].clientY - touches[1].clientY;
          return Math.sqrt(dx * dx + dy * dy);
        };

        const getMidpoint = (touches) => {
          return {
            x: (touches[0].clientX + touches[1].clientX) / 2,
            y: (touches[0].clientY + touches[1].clientY) / 2
          };
        };

        const toggleZoom = (clientX, clientY) => {
          if (scale > 1) {
            scale = MIN_SCALE;
            offsetX = 0;
            offsetY = 0;
          } else {
            scale = 3;

            const rect = image.getBoundingClientRect();
            // Point relative to the IMAGE for offset compensation
            const relativeX = clientX - rect.left;
            const relativeY = clientY - rect.top;

            // Offset compensation formula (centers zoom on tap point)
            offsetX = relativeX * (1 - scale);
            offsetY = relativeY * (1 - scale);
          }

          lastScale = scale;
          lastOffsetX = offsetX;
          lastOffsetY = offsetY;

          applyTransform();
        };

        // ----------------------------------------------------
        // 1. touchstart: Prepare the Gesture (Zoom or Pan)
        // ----------------------------------------------------
        container.addEventListener(
          'touchstart',
          (event) => {
            if (event.touches.length === 2) {
              // Zoom Gesture (2 fingers)
              event.preventDefault();
              isPanning = false;

              initialDistance = getDistance(event.touches);
              const midpoint = getMidpoint(event.touches);

              // CRITICAL FIX: Calculate the initial pinch point relative to the image's non-scaled state (Viewport-relative)
              const rect = image.getBoundingClientRect();
              initialMidpointX_Viewport = (midpoint.x - rect.left) / scale;
              initialMidpointY_Viewport = (midpoint.y - rect.top) / scale;

              lastScale = scale;
              lastOffsetX = offsetX;
              lastOffsetY = offsetY;
            } else if (event.touches.length === 1) {
              // Pan Gesture (1 finger) OR Double Tap

              // Handle Double Tap
              const now = Date.now();
              if (now - lastTap < DOUBLE_TAP_DELAY) {
                toggleZoom(event.touches[0].clientX, event.touches[0].clientY);
                event.preventDefault();
                lastTap = 0;
                isPanning = false;
                return;
              }
              lastTap = now;

              // Handle Pan (only if zoomed in)
              if (scale > 1) {
                event.preventDefault();
                isPanning = true; // Activate pan flag

                // Calculate the pan starting point to compensate for current offset (prevents jump).
                startX = event.touches[0].clientX - offsetX;
                startY = event.touches[0].clientY - offsetY;
              } else {
                isPanning = false;
              }
            }
          },
          { passive: false }
        );

        // ----------------------------------------------------
        // 2. touchmove: Execute the Gesture
        // ----------------------------------------------------
        container.addEventListener(
          'touchmove',
          (event) => {
            if (event.touches.length === 2) {
              // Zoom Gesture (2 fingers)
              event.preventDefault();
              isPanning = false; // Always disable pan during zoom

              const newDistance = getDistance(event.touches);
              const scaleChange = newDistance / initialDistance;
              const newScale = lastScale * scaleChange;

              scale = Math.min(Math.max(MIN_SCALE, newScale), MAX_SCALE);

              // CRITICAL FIX: Offset Compensation (keeps zoom centered on the pinch point)
              offsetX = lastOffsetX + initialMidpointX_Viewport * (lastScale - scale);
              offsetY = lastOffsetY + initialMidpointY_Viewport * (lastScale - scale);

              applyTransform();
            } else if (event.touches.length === 1 && scale > 1 && isPanning) {
              // Pan (1 finger) - Only if zoomed and pan flag is active
              event.preventDefault();

              const currentX = event.touches[0].clientX;
              const currentY = event.touches[0].clientY;

              // Simple drag calculation
              offsetX = currentX - startX;
              offsetY = currentY - startY;

              applyTransform();
            }
          },
          { passive: false }
        );

        // ----------------------------------------------------
        // 3. touchend: Finalize the Gesture
        // ----------------------------------------------------
        container.addEventListener('touchend', () => {
          // CRITICAL: Update state after ANY gesture to prevent jumps on the next touch
          lastScale = scale;
          lastOffsetX = offsetX;
          lastOffsetY = offsetY;
          isPanning = false; // Reset the pan flag

          // If scale is minimum, reset position.
          if (scale === MIN_SCALE) {
            offsetX = 0;
            offsetY = 0;
            applyTransform();
          }
        });
      }
    }
  );
}
